// src/api/contract-automation-api.js - Simplified version (no data transformation needed)
import { gql } from '@apollo/client';
import client from './apollo-client';

// GraphQL mutations
const GENERATE_CONTRACT_PDF = gql`
  mutation GenerateContractPdf($generateContractPdfInput: GenerateContractPdfInput!) {
    generateContractPdf(generateContractPdfInput: $generateContractPdfInput) {
      success
      message
      pdfBase64
      filename
      downloadUrl
    }
  }
`;

const CREATE_AUTOMATED_CONTRACT = gql`
  mutation CreateAutomatedContract($createAutomatedContractInput: CreateAutomatedContractInput!) {
    createAutomatedContract(createAutomatedContractInput: $createAutomatedContractInput) {
      success
      message
      contractId
      contract {
        id
        nom
        number
        dateStart
        dateEnd
        laboratoryId
        createdAt
        updatedAt
      }
      promotions {
        id
        name
        startDate
        endDate
        quantity
        status
        commissionType
        commissionAmount
        commissionPercentage
        pdfFile
        laboratoryId
        contratId
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_CONTRACT_PREVIEW = gql`
  query GetContractPreview($contractId: String!) {
    getContractPreview(contractId: $contractId)
  }
`;

/**
 * Validate contract data before sending to GraphQL
 */
const validateContractData = (data) => {
  const errors = [];

  if (!data.laboratoryId) {
    errors.push('Laboratory ID is required');
  }

  if (!Array.isArray(data.productIds) || data.productIds.length === 0) {
    errors.push('At least one product ID is required');
  }

  if (!data.contractInfo) {
    errors.push('Contract info is required');
  } else {
    if (!data.contractInfo.nom) errors.push('Contract name is required');
    if (!data.contractInfo.number) errors.push('Contract number is required');
    if (!data.contractInfo.dateStart) errors.push('Contract start date is required');
    if (!data.contractInfo.dateEnd) errors.push('Contract end date is required');
  }

  if (!Array.isArray(data.promotions) || data.promotions.length === 0) {
    errors.push('At least one promotion is required');
  } else {
    // Validate each promotion
    data.promotions.forEach((promotion, index) => {
      if (!promotion.productId) errors.push(`Promotion ${index + 1}: productId is required`);
      if (!promotion.productName) errors.push(`Promotion ${index + 1}: productName is required`);
      if (!promotion.name) errors.push(`Promotion ${index + 1}: name is required`);
      if (typeof promotion.commissionAmount !== 'number') errors.push(`Promotion ${index + 1}: commissionAmount must be a number`);
      if (typeof promotion.commissionPercentage !== 'number') errors.push(`Promotion ${index + 1}: commissionPercentage must be a number`);
      if (!promotion.commissionType) errors.push(`Promotion ${index + 1}: commissionType is required`);
    });
  }

  return errors;
};

const contractAutomationApi = {
  /**
   * Generate contract PDF
   */
  generateContractPdf: async (data) => {
    try {
      console.log('Generating contract PDF with data:', data);

      // Validate input data
      const validationErrors = validateContractData(data);
      if (validationErrors.length > 0) {
        throw new Error(`Validation errors: ${validationErrors.join(', ')}`);
      }

      // No data transformation needed - the GraphQL schema now accepts all fields
      const input = {
        laboratoryId: data.laboratoryId,
        productIds: data.productIds,
        contractInfo: {
          nom: data.contractInfo.nom,
          number: data.contractInfo.number,
          dateStart: data.contractInfo.dateStart,
          dateEnd: data.contractInfo.dateEnd
        },
        promotions: data.promotions.map(promotion => ({
          productId: promotion.productId,
          productName: promotion.productName,
          name: promotion.name,
          commissionAmount: promotion.commissionAmount,
          commissionPercentage: promotion.commissionPercentage,
          commissionType: promotion.commissionType,
          // Optional fields that are now supported by the schema
          ...(promotion.quantity !== undefined && { quantity: promotion.quantity }),
          ...(promotion.startDate && { startDate: promotion.startDate }),
          ...(promotion.endDate && { endDate: promotion.endDate }),
          ...(promotion.regionIds?.length > 0 && { regionIds: promotion.regionIds }),
        }))
      };

      console.log('Sending GraphQL input:', input);

      const { data: result } = await client.mutate({
        mutation: GENERATE_CONTRACT_PDF,
        variables: { generateContractPdfInput: input }
      });

      console.log('Contract PDF generation result:', result);
      return result.generateContractPdf;
    } catch (error) {
      console.error('Error generating contract PDF:', error);
      
      // Extract GraphQL errors if available
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        console.error('GraphQL Error Details:', graphQLError);
        throw new Error(`GraphQL Error: ${graphQLError.message}`);
      }
      
      // Extract network errors if available
      if (error.networkError) {
        console.error('Network Error Details:', error.networkError);
        throw new Error(`Network Error: ${error.networkError.message}`);
      }

      throw error;
    }
  },

  /**
   * Create automated contract with promotions in database
   */
  createAutomatedContract: async (data) => {
    try {
      console.log('Creating automated contract with data:', data);

      // Validate input data
      const validationErrors = validateContractData(data);
      if (validationErrors.length > 0) {
        throw new Error(`Validation errors: ${validationErrors.join(', ')}`);
      }

      // No data transformation needed - use full promotion data
      const input = {
        laboratoryId: data.laboratoryId,
        productIds: data.productIds,
        contractInfo: {
          nom: data.contractInfo.nom,
          number: data.contractInfo.number,
          dateStart: data.contractInfo.dateStart,
          dateEnd: data.contractInfo.dateEnd
        },
        promotions: data.promotions.map(promotion => ({
          productId: promotion.productId,
          productName: promotion.productName,
          name: promotion.name,
          commissionAmount: promotion.commissionAmount,
          commissionPercentage: promotion.commissionPercentage,
          commissionType: promotion.commissionType,
          // Optional fields
          ...(promotion.quantity !== undefined && { quantity: promotion.quantity }),
          ...(promotion.startDate && { startDate: promotion.startDate }),
          ...(promotion.endDate && { endDate: promotion.endDate }),
          ...(promotion.regionIds?.length > 0 && { regionIds: promotion.regionIds }),
        }))
      };

      console.log('Creating contract with input:', input);

      const { data: result } = await client.mutate({
        mutation: CREATE_AUTOMATED_CONTRACT,
        variables: { createAutomatedContractInput: input }
      });

      console.log('Contract creation result:', result);
      return result.createAutomatedContract;
    } catch (error) {
      console.error('Error creating automated contract:', error);
      
      // Extract GraphQL errors if available
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        console.error('GraphQL Error Details:', graphQLError);
        throw new Error(`GraphQL Error: ${graphQLError.message}`);
      }
      
      // Extract network errors if available
      if (error.networkError) {
        console.error('Network Error Details:', error.networkError);
        throw new Error(`Network Error: ${error.networkError.message}`);
      }

      throw error;
    }
  },

  /**
   * Get contract preview URL
   */
  getContractPreview: async (contractId) => {
    try {
      const { data } = await client.query({
        query: GET_CONTRACT_PREVIEW,
        variables: { contractId },
        fetchPolicy: 'network-only'
      });

      return data.getContractPreview;
    } catch (error) {
      console.error('Error getting contract preview:', error);
      throw error;
    }
  },

  /**
   * Helper method to download PDF from base64
   */
  downloadPdfFromBase64: (base64Data, filename = 'contract.pdf') => {
    try {
      // Create blob from base64
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw new Error('Failed to download PDF file');
    }
  },

  /**
   * Helper method to preview PDF from base64
   */
  previewPdfFromBase64: (base64Data) => {
    try {
      // Create blob from base64
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Open in new tab
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Cleanup after a delay to allow the browser to load the PDF
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error('Error previewing PDF:', error);
      throw new Error('Failed to preview PDF file');
    }
  },

  /**
   * Generate a unique contract number
   */
  generateContractNumber: () => {
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const randomPart = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `MAZID-${year}${month}-${randomPart}`;
  },

  /**
   * Generate contract name based on laboratory
   */
  generateContractName: (laboratoryName) => {
    if (!laboratoryName) {
      return `Contrat de partenariat MAZID`;
    }
    return `Contrat de partenariat - ${laboratoryName}`;
  },

  /**
   * Calculate default contract dates (start: today, end: 1 year from today)
   */
  getDefaultContractDates: () => {
    const today = new Date();
    const oneYearLater = new Date();
    oneYearLater.setFullYear(today.getFullYear() + 1);

    return {
      dateStart: today.toISOString().split('T')[0], // YYYY-MM-DD format
      dateEnd: oneYearLater.toISOString().split('T')[0]
    };
  },

  /**
   * Validate contract dates
   */
  validateContractDates: (dateStart, dateEnd) => {
    const errors = [];
    
    if (!dateStart) {
      errors.push('Date de début requise');
    }
    
    if (!dateEnd) {
      errors.push('Date de fin requise');
    }
    
    if (dateStart && dateEnd) {
      const start = new Date(dateStart);
      const end = new Date(dateEnd);
      const today = new Date();
      
      if (isNaN(start.getTime())) {
        errors.push('Date de début invalide');
      }
      
      if (isNaN(end.getTime())) {
        errors.push('Date de fin invalide');
      }
      
      if (start >= end) {
        errors.push('La date de fin doit être postérieure à la date de début');
      }
      
      // Warning if start date is in the past (more than 7 days)
      const daysDiff = Math.floor((today - start) / (1000 * 60 * 60 * 24));
      if (daysDiff > 7) {
        errors.push('Attention: La date de début est dans le passé');
      }
      
      // Warning if contract duration is very short or very long
      const contractDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      if (contractDays < 30) {
        errors.push('Attention: Durée du contrat très courte (moins de 30 jours)');
      }
      
      if (contractDays > 730) {
        errors.push('Attention: Durée du contrat très longue (plus de 2 ans)');
      }
    }
    
    return errors;
  }
};

export default contractAutomationApi;